import {styled} from '@linaria/react';
import React, {FC, useEffect, useRef, useState} from 'react';
import {COLORS} from '../../../constants/colors';

interface Props {
  color: string;
  backgroundColor?: string;
  count: number;
  pendingCount?: number;
  height: number;
  total: number;
  className?: string;
  style?: React.CSSProperties;
  animated?: boolean;
}

export const ProgressBar: FC<Props> = ({
  color,
  backgroundColor = COLORS.Grey4,
  count,
  pendingCount,
  height,
  total,
  animated,
  ...props
}) => {
  const progressPercentage = count / total;
  const pendingProgressPercentage = pendingCount ?? 0 / total;

  const ref = useRef<HTMLDivElement | null>(null);

  const initialProgress = animated ? 0 : progressPercentage;

  const [progress, setProgress] = useState(initialProgress);

  useEffect(() => {
    // requestAnimationFrame is needed to make sure the progress bar is always animating as sometimes the state change happens too fast
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    const id = requestAnimationFrame(() => {
      setProgress(progressPercentage);
    });

    return () => cancelAnimationFrame(id);
  }, [progressPercentage]);

  return (
    <Bar
      data-testid="progressBar"
      $backgroundColor={backgroundColor}
      $height={height}
      {...props}
    >
      <Progress
        $color={color}
        $progress={progress}
        $animated={animated}
        ref={ref}
      />
      {pendingCount !== undefined && (
        <StripedProgress
          $color={color}
          $progress={count}
          $pendingProgress={pendingProgressPercentage}
          ref={ref}
        />
      )}
    </Bar>
  );
};

const Bar = styled.div<{$backgroundColor: string; $height: number}>`
  position: relative;
  background-color: ${({$backgroundColor}) => $backgroundColor};
  height: ${({$height}) => $height}px;
  width: 100%;
  border-radius: ${({$height}) => Math.ceil($height / 2)}px;
  overflow: hidden;
`;

const Progress = styled.div<{
  $color: string;
  $progress: number;
  $animated?: boolean;
}>`
  background-color: ${({$color}) => $color};
  height: 100%;
  transform: ${({$progress}) => `scaleX(${$progress})`};
  transition: ${({$animated}) =>
    $animated ? 'transform 0.6s cubic-bezier(0.42, 0, 0.58, 1.5)' : 'unset'};

  animation-delay: 1s;
  transform-origin: top left;
`;

const StripedProgress = styled.div<{
  $color: string;
  $progress: number;
  $pendingProgress: number;
}>`
  position: absolute;
  top: 0;
  left: ${props => `${props.$progress}%`};

  width: ${props => `${props.$pendingProgress}%`};
  height: 100%;

  background: ${props => `repeating-linear-gradient(
    90deg,
    transparent,
    transparent 2px,
    ${props.$color} 2px,
    ${props.$color} 4px
  )`};
`;
